import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft, Send, Mail, Paperclip, FileText, Plus, Edit, Trash2,
  Users, Calendar, Download, TrendingUp, Clock, Image as ImageIcon, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCenter = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Email form state
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: '',
    selectedCustomer: null,
    attachUsageData: false,
    usageDataType: 'daily', // hourly, daily, monthly
    attachments: []
  });

  // Template management
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'general'
  });
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Customer email management
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [customerEmailForm, setCustomerEmailForm] = useState({
    customerId: '',
    email: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('monitoringAdminToken');
    if (!token) {
      navigate('/monitoring/login');
      return;
    }
    fetchCustomers();
    fetchTemplates();
  }, [navigate]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/customer-monitoring/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCustomerSelect = (customer) => {
    setEmailData({
      ...emailData,
      to: customer.email || '',
      selectedCustomer: customer,
      attachUsageData: false
    });
    
    if (!customer.email) {
      toast.warning(`Customer ${customer.customer_name} doesn't have an email. Please add one.`);
      setShowAddEmailModal(true);
      setCustomerEmailForm({
        customerId: customer.id,
        email: ''
      });
    }
  };

  const handleTemplateSelect = (template) => {
    setEmailData({
      ...emailData,
      subject: template.subject,
      message: template.body
    });
    toast.success('Template applied');
  };

  const handleFileAttachment = (e) => {
    const files = Array.from(e.target.files);
    setEmailData({
      ...emailData,
      attachments: [...emailData.attachments, ...files]
    });
    toast.success(`${files.length} file(s) added`);
  };

  const removeAttachment = (index) => {
    const newAttachments = emailData.attachments.filter((_, i) => i !== index);
    setEmailData({ ...emailData, attachments: newAttachments });
  };

  const handleSendEmail = async () => {
    if (!emailData.to) {
      toast.error('Please enter recipient email');
      return;
    }
    if (!emailData.subject) {
      toast.error('Please enter subject');
      return;
    }
    if (!emailData.message) {
      toast.error('Please enter message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const formData = new FormData();
      
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message);
      formData.append('attachUsageData', emailData.attachUsageData);
      formData.append('usageDataType', emailData.usageDataType);
      
      if (emailData.selectedCustomer) {
        formData.append('customerId', emailData.selectedCustomer.id);
      }
      
      // Append file attachments
      emailData.attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_URL}/api/monitoring/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Email sent successfully!');
        // Reset form
        setEmailData({
          to: '',
          subject: '',
          message: '',
          selectedCustomer: null,
          attachUsageData: false,
          usageDataType: 'daily',
          attachments: []
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Error sending email');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Please fill all template fields');
      return;
    }

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const url = editingTemplate 
        ? `${API_URL}/api/monitoring/email-templates/${editingTemplate.id}`
        : `${API_URL}/api/monitoring/email-templates`;
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success(`Template ${editingTemplate ? 'updated' : 'created'} successfully`);
        setShowTemplateModal(false);
        setTemplateForm({ name: '', subject: '', body: '', category: 'general' });
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/email-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template');
    }
  };

  const handleAddCustomerEmail = async () => {
    if (!customerEmailForm.email) {
      toast.error('Please enter email address');
      return;
    }

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/customer-monitoring/customers/${customerEmailForm.customerId}/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: customerEmailForm.email })
      });

      if (response.ok) {
        toast.success('Email added successfully');
        setShowAddEmailModal(false);
        fetchCustomers();
        
        // Update the current selection
        const updatedCustomer = customers.find(c => c.id === customerEmailForm.customerId);
        if (updatedCustomer) {
          setEmailData({
            ...emailData,
            to: customerEmailForm.email,
            selectedCustomer: { ...updatedCustomer, email: customerEmailForm.email }
          });
        }
      } else {
        toast.error('Failed to add email');
      }
    } catch (error) {
      console.error('Error adding email:', error);
      toast.error('Error adding email');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-red-50 p-6">
      {/* Stunning Header with Banner */}
      <div className="relative bg-gradient-to-r from-red-600 via-pink-600 to-yellow-500 rounded-2xl shadow-2xl overflow-hidden mb-6">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)',
          }} />
        </div>
        
        {/* Floating Icons Animation */}
        <div className="absolute top-4 right-20 opacity-20 animate-bounce">
          <Mail className="h-16 w-16 text-white" />
        </div>
        <div className="absolute bottom-4 right-40 opacity-20 animate-pulse">
          <Send className="h-12 w-12 text-white" />
        </div>
        
        <div className="relative z-10 p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Button
                onClick={() => navigate('/monitoring-dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/30 backdrop-blur-sm"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Dashboard
              </Button>
              {/* Netix Logo */}
              <div className="bg-white rounded-lg p-2 shadow-md">
                <img 
                  src="/netix-logo-horizontal.png" 
                  alt="Netix Fibernet" 
                  className="h-8 w-auto"
                />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
                    <Mail className="h-10 w-10 text-white" />
                  </div>
                  <h1 className="text-5xl font-black text-white tracking-tight">
                    Email Center
                  </h1>
                </div>
                <p className="text-xl text-white/90 font-medium ml-16">
                  ✨ Send stunning updates, reports & notifications to your customers
                </p>
              </div>
            </div>
            
            {/* Stats Badge */}
            <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 border-2 border-white/30">
              <div className="text-center">
                <div className="text-4xl font-black text-white">{customers.length}</div>
                <div className="text-sm text-white/80 font-medium">Total Customers</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" className="w-full h-12">
            <path fill="#FFF" fillOpacity="0.1" d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Email Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-2xl border-4 border-pink-200 overflow-hidden">
            {/* Card Header with Gradient */}
            <div className="bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 p-6">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-xl p-3">
                  <Send className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-white">Compose Email</h2>
                  <p className="text-white/90">Create and send beautiful emails</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer Selection */}
              <div className="bg-gradient-to-r from-yellow-50 to-pink-50 p-4 rounded-xl border-2 border-yellow-300">
                <label className="flex items-center gap-2 text-base font-bold text-gray-800 mb-3">
                  <Users className="h-5 w-5 text-pink-600" />
                  Select Customer (Optional)
                </label>
                <select
                  className="w-full border-3 border-pink-300 rounded-xl p-3 focus:ring-4 focus:ring-yellow-400 focus:border-pink-500 bg-white font-medium shadow-lg text-gray-800"
                  onChange={(e) => {
                    const customer = customers.find(c => c.id === e.target.value);
                    if (customer) handleCustomerSelect(customer);
                  }}
                  value={emailData.selectedCustomer?.id || ''}
                >
                  <option value="">🎯 -- Select Customer --</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      👤 {customer.customer_name} - {customer.static_ip} {customer.email ? `✉️ (${customer.email})` : '⚠️ (No Email)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recipient Email */}
              <div className="bg-gradient-to-r from-red-50 to-yellow-50 p-4 rounded-xl border-2 border-red-300">
                <label className="flex items-center gap-2 text-base font-bold text-gray-800 mb-3">
                  <Mail className="h-5 w-5 text-red-600" />
                  To: <span className="text-red-600 text-xl">*</span>
                </label>
                <input
                  type="email"
                  value={emailData.to}
                  onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                  placeholder="✉️ customer@example.com"
                  className="w-full border-3 border-red-300 rounded-xl p-3 focus:ring-4 focus:ring-yellow-400 focus:border-red-500 bg-white font-medium shadow-lg text-lg"
                />
                <div className="mt-3 bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-yellow-300">
                  <p className="text-sm font-semibold text-gray-700">
                    📤 <span className="text-red-600">From:</span> network@netixfibernet.com<br/>
                    📋 <span className="text-pink-600">CC:</span> data@netixworld.in<br/>
                    ↩️ <span className="text-yellow-600">Reply-To:</span> data@netixworld.in
                  </p>
                </div>
              </div>

              {/* Subject */}
              <div className="bg-gradient-to-r from-pink-50 to-red-50 p-4 rounded-xl border-2 border-pink-300">
                <label className="flex items-center gap-2 text-base font-bold text-gray-800 mb-3">
                  <FileText className="h-5 w-5 text-pink-600" />
                  Subject: <span className="text-red-600 text-xl">*</span>
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  placeholder="📝 Enter email subject"
                  className="w-full border-3 border-pink-300 rounded-xl p-3 focus:ring-4 focus:ring-red-400 focus:border-pink-500 bg-white font-medium shadow-lg text-lg"
                />
              </div>

              {/* Message */}
              <div className="bg-gradient-to-r from-yellow-50 to-red-50 p-4 rounded-xl border-2 border-yellow-300">
                <label className="flex items-center gap-2 text-base font-bold text-gray-800 mb-3">
                  <Edit className="h-5 w-5 text-yellow-600" />
                  Message: <span className="text-red-600 text-xl">*</span>
                </label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  placeholder="✍️ Type your message here..."
                  rows={10}
                  className="w-full border-3 border-yellow-300 rounded-xl p-4 focus:ring-4 focus:ring-red-400 focus:border-yellow-500 bg-white font-medium shadow-lg text-base"
                />
              </div>

              {/* Usage Data Attachment */}
              {emailData.selectedCustomer && (
                <div className="bg-gradient-to-r from-pink-100 via-yellow-100 to-red-100 border-3 border-pink-400 rounded-xl p-5 shadow-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="attachUsage"
                      checked={emailData.attachUsageData}
                      onChange={(e) => setEmailData({ ...emailData, attachUsageData: e.target.checked })}
                      className="w-6 h-6 text-pink-600 rounded-lg focus:ring-4 focus:ring-pink-400"
                    />
                    <label htmlFor="attachUsage" className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <Download className="h-5 w-5 text-pink-600" />
                      📊 Attach Customer Usage Report
                    </label>
                  </div>
                  
                  {emailData.attachUsageData && (
                    <div className="flex gap-3 bg-white rounded-xl p-4 shadow-lg">
                      <label className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white p-3 rounded-lg cursor-pointer hover:shadow-xl transition-all font-bold">
                        <input
                          type="radio"
                          name="usageType"
                          value="hourly"
                          checked={emailData.usageDataType === 'hourly'}
                          onChange={(e) => setEmailData({ ...emailData, usageDataType: e.target.value })}
                          className="w-5 h-5"
                        />
                        <Clock className="h-5 w-5" />
                        ⏰ Hourly
                      </label>
                      <label className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-pink-400 to-pink-500 text-white p-3 rounded-lg cursor-pointer hover:shadow-xl transition-all font-bold">
                        <input
                          type="radio"
                          name="usageType"
                          value="daily"
                          checked={emailData.usageDataType === 'daily'}
                          onChange={(e) => setEmailData({ ...emailData, usageDataType: e.target.value })}
                          className="w-5 h-5"
                        />
                        <Calendar className="h-5 w-5" />
                        📅 Daily
                      </label>
                      <label className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-red-400 to-red-500 text-white p-3 rounded-lg cursor-pointer hover:shadow-xl transition-all font-bold">
                        <input
                          type="radio"
                          name="usageType"
                          value="monthly"
                          checked={emailData.usageDataType === 'monthly'}
                          onChange={(e) => setEmailData({ ...emailData, usageDataType: e.target.value })}
                          className="w-5 h-5"
                        />
                        <TrendingUp className="h-5 w-5" />
                        📈 Monthly
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* File Attachments */}
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-xl border-2 border-red-300">
                <label className="flex items-center gap-2 text-base font-bold text-gray-800 mb-3">
                  <Paperclip className="h-5 w-5 text-red-600" />
                  📎 Attachments
                </label>
                <div className="border-4 border-dashed border-red-300 rounded-xl p-6 bg-white hover:bg-red-50 transition-all">
                  <input
                    type="file"
                    id="fileInput"
                    multiple
                    onChange={handleFileAttachment}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  />
                  <Button
                    onClick={() => document.getElementById('fileInput').click()}
                    className="w-full bg-gradient-to-r from-red-500 via-pink-500 to-yellow-500 hover:from-red-600 hover:via-pink-600 hover:to-yellow-600 text-white font-bold text-lg py-4 shadow-xl"
                  >
                    <Paperclip className="h-5 w-5 mr-2" />
                    📁 Click to Attach Files / Images
                  </Button>
                  
                  {emailData.attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {emailData.attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gradient-to-r from-yellow-100 to-pink-100 p-3 rounded-lg border-2 border-pink-300 shadow-md">
                          <div className="flex items-center gap-3">
                            {file.type.startsWith('image/') ? (
                              <div className="bg-blue-500 rounded-lg p-2">
                                <ImageIcon className="h-5 w-5 text-white" />
                              </div>
                            ) : (
                              <div className="bg-gray-500 rounded-lg p-2">
                                <FileText className="h-5 w-5 text-white" />
                              </div>
                            )}
                            <div>
                              <span className="text-sm font-bold text-gray-800 block">{file.name}</span>
                              <span className="text-xs text-gray-600">📊 {(file.size / 1024).toFixed(1)} KB</span>
                            </div>
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-lg p-2 transition-all shadow-md"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="w-full bg-gradient-to-r from-red-600 via-pink-600 to-yellow-500 hover:from-red-700 hover:via-pink-700 hover:to-yellow-600 text-white font-black text-2xl py-6 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border-4 border-white"
              >
                {sending ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full" />
                    <span>📤 Sending Your Amazing Email...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <Send className="h-8 w-8" />
                    <span>🚀 SEND EMAIL NOW!</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar - Templates & Quick Actions */}
        <div className="space-y-6">
          {/* Email Templates */}
          <div className="bg-white rounded-2xl shadow-2xl border-4 border-yellow-200 overflow-hidden">
            <div className="bg-gradient-to-r from-yellow-500 via-red-500 to-pink-500 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="bg-white rounded-xl p-2">
                    <FileText className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 className="text-2xl font-black text-white">Templates</h3>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateForm({ name: '', subject: '', body: '', category: 'general' });
                    setShowTemplateModal(true);
                  }}
                  className="bg-white text-red-600 hover:bg-red-50 font-bold shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  ✨ New
                </Button>
              </div>
            </div>
            <div className="p-4">
              {templates.length === 0 ? (
                <div className="text-center py-8 bg-gradient-to-r from-yellow-50 to-pink-50 rounded-xl border-2 border-dashed border-yellow-300">
                  <FileText className="h-16 w-16 text-yellow-400 mx-auto mb-3" />
                  <p className="text-lg font-bold text-gray-700">📝 No templates yet</p>
                  <p className="text-sm text-gray-600">Create your first template!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {templates.map(template => (
                    <div key={template.id} className="bg-gradient-to-r from-yellow-50 to-pink-50 border-3 border-yellow-300 rounded-xl p-4 hover:shadow-xl transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-black text-base text-gray-900">📄 {template.name}</p>
                          <p className="text-sm text-gray-700 mt-1 font-medium">✉️ {template.subject}</p>
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              onClick={() => handleTemplateSelect(template)}
                              className="bg-gradient-to-r from-pink-500 to-red-500 text-white font-bold shadow-lg hover:shadow-xl"
                            >
                              ✨ Use
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setEditingTemplate(template);
                                setTemplateForm(template);
                                setShowTemplateModal(true);
                              }}
                              className="bg-yellow-500 text-white font-bold shadow-lg hover:bg-yellow-600"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="bg-red-600 text-white font-bold shadow-lg hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-2xl shadow-2xl border-4 border-pink-200 overflow-hidden">
            <div className="bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 p-5">
              <div className="flex items-center gap-2">
                <div className="bg-white rounded-xl p-2">
                  <TrendingUp className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-2xl font-black text-white">📊 Quick Stats</h3>
              </div>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-gradient-to-r from-blue-100 to-cyan-100 p-3 rounded-xl border-2 border-blue-300 shadow-md">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="font-bold text-gray-800">Total Customers</span>
                  </div>
                  <span className="text-2xl font-black text-blue-600">{customers.length}</span>
                </div>
                <div className="flex items-center justify-between bg-gradient-to-r from-green-100 to-emerald-100 p-3 rounded-xl border-2 border-green-300 shadow-md">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-green-600" />
                    <span className="font-bold text-gray-800">With Email</span>
                  </div>
                  <span className="text-2xl font-black text-green-600">{customers.filter(c => c.email).length}</span>
                </div>
                <div className="flex items-center justify-between bg-gradient-to-r from-orange-100 to-red-100 p-3 rounded-xl border-2 border-orange-300 shadow-md">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                    <span className="font-bold text-gray-800">Without Email</span>
                  </div>
                  <span className="text-2xl font-black text-orange-600">{customers.filter(c => !c.email).length}</span>
                </div>
                <div className="flex items-center justify-between bg-gradient-to-r from-purple-100 to-pink-100 p-3 rounded-xl border-2 border-purple-300 shadow-md">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    <span className="font-bold text-gray-800">Templates</span>
                  </div>
                  <span className="text-2xl font-black text-purple-600">{templates.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    placeholder="e.g., Monthly Usage Report"
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="general">General</option>
                    <option value="usage">Usage Report</option>
                    <option value="billing">Billing</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="promotion">Promotion</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                    placeholder="Email subject line"
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body *
                  </label>
                  <textarea
                    value={templateForm.body}
                    onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                    placeholder="Email body..."
                    rows={8}
                    className="w-full border border-gray-300 rounded-md p-3"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSaveTemplate}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
                <Button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Email Modal */}
      {showAddEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Add Customer Email</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={customerEmailForm.email}
                    onChange={(e) => setCustomerEmailForm({ ...customerEmailForm, email: e.target.value })}
                    placeholder="customer@example.com"
                    className="w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleAddCustomerEmail}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Add Email
                </Button>
                <Button
                  onClick={() => setShowAddEmailModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCenter;
